import {
  EnhancedSeoHead,
  hasProviderCaireIndexSignal,
  ProviderCaireIndexPanel,
  resolveProviderCaireIndex,
} from "@appcaire/shared/seo";
import {
  Check,
  ExternalLink,
  Link2,
  MapPin,
  Phone,
  Printer,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useStockholmEventTracking } from "../lib/analytics";
import {
  formatSourceDate,
  providerDistricts,
  type StockholmProvider,
  useStockholmProviders,
} from "../lib/providers";
import { SITE_ORIGIN } from "../lib/site";
import { useShortlist } from "../shortlist/ShortlistContext";
import { DataUnavailable, PageLoading } from "./PageStates";

const CAIRE_INDEX_METHODOLOGY_URL =
  "https://sverigeshemtjanst.se/topplistor/metodik";

function valueOrMissing(
  value: string | number | null | undefined,
  suffix = "",
) {
  return value === null || value === undefined || value === ""
    ? "Uppgift saknas"
    : `${value}${suffix}`;
}

function isAvailable(provider: StockholmProvider): boolean {
  return provider.presence.status.trim().toUpperCase() === "ACTIVE";
}

export default function ComparePage() {
  const query = useStockholmProviders();
  const shortlist = useShortlist();
  const reconcileAvailable = shortlist.reconcileAvailable;
  const trackStockholmEvent = useStockholmEventTracking();
  const [shareMessage, setShareMessage] = useState<string | null>(null);

  const availableProviders = query.providers.filter(isAvailable);
  const providers = shortlist.providerIds.flatMap((id) => {
    const provider = availableProviders.find(
      (candidate) => candidate.id === id,
    );
    return provider ? [provider] : [];
  });

  const availableProviderKey = availableProviders
    .map((provider) => provider.id)
    .join("|");

  useEffect(() => {
    if (!shortlist.isHydrated || query.loading || query.error) return;
    reconcileAvailable(
      availableProviderKey ? availableProviderKey.split("|") : [],
    );
  }, [
    availableProviderKey,
    query.error,
    query.loading,
    reconcileAvailable,
    shortlist.isHydrated,
  ]);

  useEffect(() => {
    if (providers.length > 0)
      trackStockholmEvent("compare_view", {
        provider_count: providers.length,
        source_surface: "compare",
      });
  }, [providers.length, trackStockholmEvent]);

  if (query.loading) return <PageLoading label="Förbereder jämförelsen…" />;
  if (query.error) return <DataUnavailable path="/jamfor" />;

  const shareUrl = `${SITE_ORIGIN}${shortlist.compareHref}`;
  const share = async () => {
    try {
      if (navigator.share) {
        trackStockholmEvent("share_shortlist", {
          provider_count: providers.length,
          source_surface: "compare",
          channel: "native_share",
        });
        await navigator.share({
          title: "Min jämförelse av hemtjänst i Stockholm",
          url: shareUrl,
        });
        setShareMessage("Jämförelsen delades.");
      } else {
        trackStockholmEvent("share_shortlist", {
          provider_count: providers.length,
          source_surface: "compare",
          channel: "clipboard",
        });
        await navigator.clipboard.writeText(shareUrl);
        setShareMessage("Länken kopierades.");
      }
    } catch {
      setShareMessage("Det gick inte att dela länken just nu.");
    }
  };

  return (
    <>
      <EnhancedSeoHead
        siteKey="stockholm"
        title="Jämför hemtjänst i Stockholm"
        description="Jämför upp till tre sparade utförare sida vid sida. Jämförelsen sparas i din webbläsare och kan delas med en närstående."
        canonicalUrl={`${SITE_ORIGIN}/jamfor`}
        indexable={false}
        contentClassification="Personal comparison tool"
        targetAudience="seniors, relatives"
      />
      <header className="border-b border-border bg-accent/65 print:bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <p className="text-sm font-bold uppercase tracking-wider text-brand-secondary">
            Din kortlista
          </p>
          <h1 className="mt-3 font-[var(--font-editorial)] text-4xl font-bold text-foreground md:text-5xl">
            Jämför utförare
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-muted-foreground">
            Välj högst tre utförare. Saknas en uppgift skriver vi det.
            Personalkontinuitet visar hur många olika personer som hjälper dig
            under två veckor – färre är bättre. CaireIndex är ett sammanvägt
            jämförelsestöd, inte ett kundomdöme.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Uppdaterad: {formatSourceDate(query.sourceDate)}.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 print:hidden">
            <button
              type="button"
              onClick={share}
              disabled={providers.length === 0}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-brand-primary px-5 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
            >
              <Link2 className="h-4 w-4" aria-hidden="true" /> Dela länk
            </button>
            <button
              type="button"
              onClick={() => {
                trackStockholmEvent("print_shortlist", {
                  provider_count: providers.length,
                  source_surface: "compare",
                });
                window.print();
              }}
              disabled={providers.length === 0}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-brand-primary/30 bg-white px-5 py-3 font-semibold text-brand-primary disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            >
              <Printer className="h-4 w-4" aria-hidden="true" /> Skriv ut
            </button>
            {providers.length > 0 && (
              <button
                type="button"
                onClick={shortlist.clear}
                className="inline-flex min-h-11 items-center gap-2 rounded-xl px-4 py-3 font-semibold text-muted-foreground hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Rensa
              </button>
            )}
          </div>
          {shareMessage && (
            <p
              role="status"
              className="mt-3 text-sm font-semibold text-brand-primary print:hidden"
            >
              {shareMessage}
            </p>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {providers.length === 0 ? (
          <section className="rounded-3xl border border-dashed border-brand-primary/35 bg-card p-10 text-center">
            <X
              className="mx-auto h-10 w-10 text-muted-foreground"
              aria-hidden="true"
            />
            <h2 className="mt-4 text-2xl font-bold text-foreground">
              Du har inte sparat någon utförare ännu
            </h2>
            <p className="mx-auto mt-3 max-w-xl leading-7 text-muted-foreground">
              Öppna listan och välj Spara på de utförare du är intresserad av.
              Din lista finns bara i din webbläsare och skickas aldrig till oss.
            </p>
            <Link
              to="/hitta-hemtjanst"
              className="mt-6 inline-flex min-h-11 items-center rounded-xl bg-brand-primary px-6 py-3 font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
            >
              Hitta hemtjänst
            </Link>
          </section>
        ) : (
          <div className="grid gap-5 lg:grid-cols-3">
            {providers.map((provider) => {
              const quality = provider.presence.latestQuality;
              const districts = providerDistricts(provider);
              const caireIndex = resolveProviderCaireIndex(provider, {
                slug: "stockholm",
              });
              return (
                <article
                  key={provider.id}
                  className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm break-inside-avoid"
                >
                  <div className="border-b border-border bg-brand-primary/5 p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-brand-secondary">
                          Utförare
                        </p>
                        <h2 className="mt-1 text-2xl font-bold text-foreground">
                          {provider.displayName || provider.name}
                        </h2>
                      </div>
                      <button
                        type="button"
                        onClick={() => shortlist.remove(provider.id)}
                        aria-label={`Ta bort ${provider.name}`}
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-muted-foreground hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary print:hidden"
                      >
                        <Trash2 className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                    <Link
                      to={`/utforare/${provider.slug}`}
                      className="mt-5 inline-flex min-h-11 items-center gap-2 font-semibold text-brand-primary underline-offset-4 hover:underline print:hidden"
                    >
                      Öppna profilen{" "}
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>
                  {hasProviderCaireIndexSignal(caireIndex) && (
                    <div className="border-b border-border p-4">
                      <ProviderCaireIndexPanel
                        value={caireIndex}
                        methodologyHref={CAIRE_INDEX_METHODOLOGY_URL}
                        onMethodologyClick={() =>
                          trackStockholmEvent("seo_funnel_click", {
                            provider_id: provider.id,
                            source_surface: "compare",
                            target_path: "/topplistor/metodik",
                          })
                        }
                        compact
                      />
                    </div>
                  )}
                  <dl className="divide-y divide-border">
                    <div className="p-5">
                      <dt className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                        <MapPin className="h-4 w-4" aria-hidden="true" />{" "}
                        Stadsdelar
                      </dt>
                      <dd className="mt-2 font-medium text-foreground">
                        {districts.length
                          ? districts.join(", ")
                          : "Uppgift saknas"}
                      </dd>
                    </div>
                    <div className="p-5">
                      <dt className="text-sm font-semibold text-muted-foreground">
                        Kommunal eller privat
                      </dt>
                      <dd className="mt-2 font-medium text-foreground">
                        {valueOrMissing(
                          provider.orgType === "MUNICIPAL"
                            ? "Kommunal"
                            : provider.orgType === "PRIVATE"
                              ? "Privat"
                              : provider.orgType,
                        )}
                      </dd>
                    </div>
                    <div className="p-5">
                      <dt className="text-sm font-semibold text-muted-foreground">
                        Språk
                      </dt>
                      <dd className="mt-2 font-medium text-foreground">
                        {provider.languages?.length
                          ? provider.languages.join(", ")
                          : "Uppgift saknas"}
                      </dd>
                    </div>
                    <div className="p-5">
                      <dt className="text-sm font-semibold text-muted-foreground">
                        Särskild kompetens
                      </dt>
                      <dd className="mt-2 font-medium text-foreground">
                        {provider.specialCompetencies?.length
                          ? provider.specialCompetencies.join(", ")
                          : "Uppgift saknas"}
                      </dd>
                    </div>
                    <div className="p-5">
                      <dt className="text-sm font-semibold text-muted-foreground">
                        Kundnöjdhet
                      </dt>
                      <dd className="mt-2 font-medium text-foreground">
                        {valueOrMissing(quality?.customerSatisfaction, "%")}
                      </dd>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {quality
                          ? `${quality.source}, ${quality.year}`
                          : "Källa saknas"}
                      </p>
                    </div>
                    <div className="p-5">
                      <dt className="text-sm font-semibold text-muted-foreground">
                        Olika personer som hjälper dig
                      </dt>
                      <dd className="mt-2 font-medium text-foreground">
                        {quality?.staffContinuity == null
                          ? "Uppgift saknas"
                          : `${quality.staffContinuity.toLocaleString("sv-SE", { maximumFractionDigits: 1 })} olika personer på två veckor`}
                      </dd>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Färre är bättre.
                      </p>
                    </div>
                    <div className="p-5">
                      <dt className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                        <Phone className="h-4 w-4" aria-hidden="true" /> Kontakt
                      </dt>
                      <dd className="mt-2 space-y-1 font-medium text-foreground">
                        {provider.phone ? (
                          <a
                            href={`tel:${provider.phone}`}
                            className="block underline"
                          >
                            {provider.phone}
                          </a>
                        ) : (
                          "Uppgift saknas"
                        )}
                        {provider.website && (
                          <a
                            href={provider.website}
                            target="_blank"
                            rel="noreferrer"
                            className="block text-brand-primary underline"
                          >
                            Webbplats
                          </a>
                        )}
                      </dd>
                    </div>
                  </dl>
                </article>
              );
            })}
          </div>
        )}

        {providers.length > 0 && (
          <aside className="mt-8 flex gap-3 rounded-2xl border border-brand-primary/20 bg-brand-primary/5 p-5 text-sm leading-6 text-muted-foreground">
            <Check
              className="mt-0.5 h-5 w-5 shrink-0 text-brand-primary"
              aria-hidden="true"
            />
            <p>
              Jämförelsen hjälper dig att välja – den är ingen garanti för att
              en utförare kan ta emot dig. Hör med Stockholms stad om vad som
              gäller och fråga utföraren om det som är viktigast för dig.
            </p>
          </aside>
        )}
      </div>
    </>
  );
}
